import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useClientPortalPaths } from "../../hooks/useClientPortalBase";
import { getOpportunityReport, exportReportCsv } from "../../api/clientAnalyticsApi";
import ClientAnalyticsDateRange from "../../components/clientPortal/ClientAnalyticsDateRange";
import {
  ClientPortalError,
  ClientPortalLoading,
} from "../../components/clientPortal/ClientPortalStates";
import { buildAnalyticsQuery } from "../../utils/clientAnalyticsQuery";
import { formatClientDate } from "../../utils/clientPortalFormat";

const ClientOpportunityReportPage = () => {
  const { clientViewId } = useParams();
  const [preset, setPreset] = useState("30d");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [status, setStatus] = useState("");
  const [needsFollowUp, setNeedsFollowUp] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const paths = useClientPortalPaths();

  const query = {
    ...buildAnalyticsQuery({ preset, dateFrom, dateTo }),
    status: status || undefined,
    needsFollowUp: needsFollowUp || undefined,
    pageSize: 50,
  };

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await getOpportunityReport(clientViewId, query);
      setData(body);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load report.");
    } finally {
      setLoading(false);
    }
  }, [clientViewId, preset, dateFrom, dateTo, status, needsFollowUp]);

  useEffect(() => {
    load();
  }, [load]);

  const handleExport = async () => {
    const blob = await exportReportCsv(clientViewId, {
      ...query,
      reportType: "opportunities",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "opportunities-report.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <section className="panel-card">
        <p className="eyebrow">
          <Link to={paths.viewSection(clientViewId, "reports")}>Reports</Link> / Opportunities
        </p>
        <div className="client-portal-section-header">
          <h2>Opportunity Report</h2>
          <button type="button" className="midpoint-share-button secondary" onClick={handleExport}>
            Export CSV
          </button>
        </div>
        <ClientAnalyticsDateRange
          preset={preset}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onChange={({ preset: p, dateFrom: f, dateTo: t }) => {
            if (p != null) setPreset(p);
            if (f != null) setDateFrom(f);
            if (t != null) setDateTo(t);
          }}
        />
        <div className="client-portal-filters">
          <label className="admin-users-field">
            <span>Status</span>
            <input value={status} onChange={(e) => setStatus(e.target.value)} placeholder="e.g. new" />
          </label>
          <label className="admin-users-field client-portal-checkbox-field">
            <span>Needs follow-up only</span>
            <input
              type="checkbox"
              checked={needsFollowUp}
              onChange={(e) => setNeedsFollowUp(e.target.checked)}
            />
          </label>
        </div>
      </section>

      {loading ? <ClientPortalLoading message="Loading report…" /> : null}
      {errorMessage ? <ClientPortalError message={errorMessage} /> : null}

      {data?.opportunities ? (
        <div className="panel-card">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Submitted</th>
                <th>Contact</th>
                <th>Type</th>
                <th>Status</th>
                <th>Community</th>
                <th>Course</th>
                <th>Source</th>
                <th>Follow-up</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.opportunities.map((o) => (
                <tr key={o.opportunityId}>
                  <td>{formatClientDate(o.submittedAt)}</td>
                  <td>
                    {o.contactName}
                    <br />
                    <span className="muted">{o.contactEmail}</span>
                  </td>
                  <td>{o.opportunityType || "—"}</td>
                  <td>{o.statusLabel || o.status}</td>
                  <td>{o.communityInterest || "—"}</td>
                  <td>{o.courseInterest || "—"}</td>
                  <td>{o.source || "—"}</td>
                  <td>{o.nextFollowUpAt ? formatClientDate(o.nextFollowUpAt) : "—"}</td>
                  <td>
                    <Link
                      to={paths.viewSection(clientViewId, "opportunities", o.opportunityId)}
                      className="cv-button-link"
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
};

export default ClientOpportunityReportPage;
